import { randomUUID } from 'crypto';
import { Command } from 'commander';
import { ApiClient } from '../client.js';
import { formatOutput } from '../output.js';
import { getBase, getFormat } from '../helpers.js';
import { confirm } from '../prompt.js';

export const inboundWebhooksCommand = new Command('inbound-webhooks')
  .description('Manage inbound webhook endpoints');

inboundWebhooksCommand.command('create')
  .description('Create a new inbound webhook')
  .requiredOption('--name <name>', 'Inbound webhook name')
  .requiredOption('--secret <secret>', 'HMAC shared secret')
  .option('--id <id>', 'Inbound webhook ID (auto-generated if omitted)')
  .option('--record_type-scope <record_types>', 'Comma-separated record_type scope')
  .option('--field-mapping <json>', 'Inline mapping applied to the payload before write — a field_mapping JSON, e.g. {"to_external":{"status":"state"}} (to_external renames auto-invert on read) or {"to_rekor":{...}}/{"computed":{...}}. Mutually exclusive with --source-binding.')
  .option('--source-binding <json>', 'Reuse a record_type source\'s field_mapping for the to_rekor translation — {"record_type":"<id>","source":"<name>"}. Mutually exclusive with --field-mapping.')
  .option('--ingest-auth <json>', 'Inbound auth scheme. Default (omitted) = HMAC signature. Static-header senders: {"type":"static_header","header":"X-Account-Key"} — the configured header is compared to --secret (constant-time) instead of an HMAC.')
  .option('--hydration <json>', 'Make this a reference-style ("notification + fetch") webhook: a thin delivery names a record id; Rekor fetches the full record via the bound source\'s read endpoint and stores it. Requires --source-binding and a single native --record_type-scope. JSON: {"id_path":"record_id","event_path":"event","event_map":{"ResourceDeleted":"delete"},"default_op":"upsert","merge":true}. Set "merge":true to refresh only the mapped (upstream-owned) fields on re-sync and preserve local rekor-owned fields (status/tags/scores) on the same record.')
  .action(async function (this: Command, opts: { name: string; secret: string; id?: string; recordTypeScope?: string; fieldMapping?: string; sourceBinding?: string; ingestAuth?: string; hydration?: string }) {
    const ws = getBase(this);
    const client = new ApiClient();
    const inboundWebhookId = opts.id ?? randomUUID();
    const body: Record<string, unknown> = {
      name: opts.name,
      secret: opts.secret,
      enabled: true,
    };
    if (opts.recordTypeScope) {
      body['record_type_scope'] = opts.recordTypeScope.split(',');
    }
    if (opts.fieldMapping) body['field_mapping'] = JSON.parse(opts.fieldMapping);
    if (opts.sourceBinding) body['source_binding'] = JSON.parse(opts.sourceBinding);
    if (opts.ingestAuth) body['ingest_auth'] = JSON.parse(opts.ingestAuth);
    if (opts.hydration) body['hydration'] = JSON.parse(opts.hydration);
    const data = await client.request('PUT', `/v1/${ws}/inbound-webhooks/${inboundWebhookId}`, body);
    console.log(formatOutput(data, getFormat(this)));
  });

inboundWebhooksCommand.command('get <id>')
  .description('Get an inbound webhook')
  .action(async function (this: Command, id: string) {
    const ws = getBase(this);
    const client = new ApiClient();
    const data = await client.request('GET', `/v1/${ws}/inbound-webhooks/${id}`);
    console.log(formatOutput(data, getFormat(this)));
  });

inboundWebhooksCommand.command('list')
  .description('List all inbound webhooks')
  .action(async function (this: Command) {
    const ws = getBase(this);
    const client = new ApiClient();
    const data = await client.request('GET', `/v1/${ws}/inbound-webhooks`);
    console.log(formatOutput(data, getFormat(this)));
  });

inboundWebhooksCommand.command('deliveries')
  .description('List hydration delivery status for reference-style ("notification + fetch") webhooks')
  .option('--status <status>', 'Filter by status (pending|delivered|failed|dead)')
  .option('--inbound-webhook <id>', 'Filter to one inbound webhook')
  .action(async function (this: Command, opts: { status?: string; inboundWebhook?: string }) {
    const ws = getBase(this);
    const client = new ApiClient();
    const params = new URLSearchParams();
    if (opts.status) params.set('status', opts.status);
    if (opts.inboundWebhook) params.set('inbound_webhook_id', opts.inboundWebhook);
    const qs = params.toString() ? `?${params.toString()}` : '';
    const data = await client.request('GET', `/v1/${ws}/inbound-webhooks/deliveries${qs}`);
    console.log(formatOutput(data, getFormat(this)));
  });

inboundWebhooksCommand.command('delete <id>')
  .description('Delete an inbound webhook')
  .option('-y, --yes', 'Skip confirmation prompt')
  .action(async function (this: Command, id: string, opts: { yes?: boolean }) {
    if (!opts.yes && !(await confirm(`Delete inbound webhook ${id}? This cannot be undone.`))) {
      console.log('Aborted');
      return;
    }
    const ws = getBase(this);
    const client = new ApiClient();
    await client.request('DELETE', `/v1/${ws}/inbound-webhooks/${id}`);
    console.log('Deleted');
  });
